import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import CommitteeMemberCard from '../../components/blocks/CommitteeMemberCard';
import FacultyAdvisorCard from '../../components/blocks/FacultyAdvisorCard';
import { getCommitteeMembersWithRoles, JELTR_EDITORIAL_BOARD } from '../../config/committees';
import { getAdvisorsByKeys, JELTR_HONORARY_ADVISOR_KEYS } from '../../config/advisors';

const Jeltr = () => {
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="flex justify-center mb-1.5">
                        <img src="/asset/icons/JELTR_Logo_Full_Coloured.svg" alt="JELTR Logo" className="h-60 w-auto" />
                    </div>
                    {/* <h1 className="text-4xl font-bold text-gray-900 mb-4">JELTR</h1> */}
                    <p className="text-xl text-gray-600 max-w-5xl mx-auto text-center mb-6">
                        Journal of Experiential Learning & Transdisciplinary Research - A publication-oriented student body
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href="mailto:titanprojectshk@gmail.com"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-600 transition"
                        >
                            Email Us
                        </a>
                        <a
                            href="https://instagram.com/jeltr.hk"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-purple-400 hover:text-purple-600 transition"
                        >
                            Follow on Instagram
                        </a>
                        <a
                            href="https://www.linkedin.com/showcase/journal-of-experiential-learning-transdisciplinary-research/posts/?feedView=all"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-blue-600 hover:text-blue-600 transition"
                        >
                            Follow on LinkedIn
                        </a>
                    </div>
                </div>

                {/* What is JELTR Section */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">What is JELTR?</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="md:col-span-2">
                                <div className="grid md:grid-cols-1 gap-8 mb-4">
                                    <div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Mission</h3>
                                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                            The Journal of Experiential Learning and Transdisciplinary Research (JETLR) publishes visionary academic articles by Undergraduate students in Hong Kong, focusing on themes related to Experiential Learning and Transdisciplinary Research. As a part of the Transdisciplinary Initiative for Talents and Innovators (TITAN), the operation of JETLR is also accredited by the Millennium Fellowship 2025 (UNAI x MCN).
                                        </p>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Vision</h3>
                                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                                            We believe in the potential of Undergraduate students to yield academic insights that inspire new threads of thought in academia. JETLR is positioned as a springboard for emerging young scholars to formally enter the field of academic research. We aim to be the bridge between research experts and young intellectuals, traditional disciplines and new methodologies, theoretical monolith and experiential insights.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="md:col-span-1">
                                <div className="sticky top-6">
                                    <img
                                        src="/asset/images/posters/JELTR.jpeg"
                                        alt="JELTR Poster"
                                        className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Major themes */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Major themes</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="space-y-8">
                            <div className="border-l-4 border-green-500 pl-6 rounded-lg transition-all duration-200 hover:bg-green-50 hover:border-green-600 hover:shadow-md">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Transdisciplinary Research</h3>
                                <p className="text-gray-700 text-justify leading-relaxed">
                                    Transdisciplinary research is often celebrated as a progressive ideal, yet it becomes ever more indispensable in a world where our gravest difficulties spill messily across the neat borders of established disciplines. All too often, such difficulties are reduced to technical puzzles for isolated experts, as if climate change were merely a question of atmospheric modelling, or a matter of clinical protocols, while the intricate weave of social, cultural, political and ethical forces through which these problems endure is quietly set aside. Transdisciplinarity seeks to restore the missing linkages by convening scientists, humanists, professionals and community members in a shared endeavour. Transdisciplinary research is not merely a method for producing more comprehensive reports; it provides a more in-depth understanding of the world and people around us and offers a better chance of solving problems in society, arts, science, and beyond.
                                </p>
                                <div className="mt-5 bg-white/60 rounded-lg p-4 transition-colors duration-200 hover:bg-green-50">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                        Recommended (but not limited to) transdisciplinary sub-themes
                                    </h4>
                                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                                        <li>AI, Big Data and Society</li>
                                        <li>Law, Humanities and Global Challenges</li>
                                        <li>China, Culture and Heritage in a Changing World</li>
                                        <li>Smart cities and Urban Ecologies</li>
                                        <li>Scientific Innovation and Industry in the modern world</li>
                                        <li>Biological and Medical Sciences for Global Good</li>
                                        <li>General transdisciplinary themes</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="border-l-4 border-blue-500 pl-6 rounded-lg transition-all duration-200 hover:bg-blue-50 hover:border-blue-600 hover:shadow-md">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">Experiential Learning</h3>
                                <p className="text-gray-700 text-justify leading-relaxed">
                                    As one of the most commonly recognized pedagogies in contemporary society, Experiential Learning (EL) theory proposes a mode of learning through actual experience, active inquiry and reflection. The study of Experiential Learning involves both empirical and statistical investigation of students' change in domains such as knowledge, cultural awareness, social responsibility and more. This is where a transdisciplinary lens could be used for research. JETLR is hence committed to publishing academic findings and evaluations of various EL activities. By revisiting past theories and activities of Experiential Learning, one may better understand the nature of education and the environment that has nurtured us. It is an opportunity for us to reexamine current pedagogies and propose insights for better future education.
                                </p>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Call for Submission */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Call for Submission</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-3 gap-8 items-center">
                            <div className="md:col-span-2">
                                <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                                    Submission of original, scholarly research articles is open to, but not limited to, undergraduates from all tertiary education institutions. For further information, feel free to communicate with our editorial team at{' '}
                                    <a href="mailto:titanprojectshk@gmail.com" className="text-orange-600 hover:text-orange-700 font-medium underline">
                                        titanprojectshk@gmail.com
                                    </a>.
                                </p>
                                <Link
                                    to="/jeltr/call-for-papers-2026"
                                    className="inline-block px-6 py-3 rounded-full border border-purple-400 text-purple-700 font-semibold hover:bg-purple-50 hover:border-purple-500 transition"
                                >
                                    JELTR Call for Papers 2026
                                </Link>
                            </div>
                            <div className="md:col-span-1">
                                <div className="sticky top-6">
                                    <img
                                        src="/asset/images/events/jeltr/papers-2026/Poster.png"
                                        alt="JELTR Call for Papers 2026 Poster"
                                        className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Editorial Board */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Editorial Board</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
                            Our editorial board consists of dedicated students and faculty members who ensure the quality and academic rigor of JELTR publications.
                        </p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {getCommitteeMembersWithRoles(JELTR_EDITORIAL_BOARD).map((member) => (
                                <CommitteeMemberCard key={member.name} {...member} />
                            ))}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Honorary Advisors */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Honorary Advisors</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
                            Our honorary advisors provide academic guidance and support to ensure the academic rigor and quality of JELTR publications.
                        </p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {getAdvisorsByKeys(JELTR_HONORARY_ADVISOR_KEYS, 'purple').map((advisor) => (
                                <FacultyAdvisorCard key={advisor.name} {...advisor} />
                            ))}
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default Jeltr;

